import React, { FC, useContext, useEffect, useState } from "react";
import {
  backend,
  BackendResponse,
  categoryDateRange,
  ClusterType,
  displayDateLoose,
  GetCaseEntityBlocksResponse,
  GetCaseMatchingBlocksResponse,
  GetLensRunResponse,
  LensHit,
  makeLensHitFilters,
  MatchingBlock,
  PageType,
} from "Backend";
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ViewStreamIcon from "@mui/icons-material/ViewStream";
import HitsBrowserLayout from "../HitsBrowserLayout";
import InfoIcon from "@mui/icons-material/Info";
import { distinct, empty, nonEmpty } from "helpers/coll";
import { LensHitFilterState, numberOfLensHitFilters } from "../LensHitFilterState";
import Flex from "components/Flex";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import HitCardVirtuoso from "./HitCardVirtuoso";
import PinHitsTableDialog from "./PinHitsTableDialog";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { calculateQueryParams, loadQueryParams } from "./ReviewQueryParams";
import LensHitFilterControls from "../LensHitFilterControls";
import { P } from "theme/typography";
import CaseHitsBreadcrumbs from "./CaseHitsBreadcrumbs";
import LensCategories from "./LensCategory";
import { Block, Case, CategoryFindingSummary, DistinctMode, DocumentStatus, Form, FormField } from "generated/graphql";
import PharmacyFindings from "./PharmacyFindings";
import { fetchReviewConfig, selectForm, selectReviewConfigByField } from "redux/slices/form.slice";
import {
  selectReviewBaseFindingsStatus,
  selectReviewCurrentFindings,
  selectReviewFilteredFindingsStatus,
  fetchBaseFindings,
  fetchFilteredFindings,
  getCaseFindings,
} from "redux/slices/case";
import IntakeViewer from "components/DocumentViewer/IntakeViewer";
import ProfileBasedReviewControl from "./ProfileBasedReviewControl";
import RequestStatus from "redux/slices/RequestStatus";
import LoadingSpinner from "components/LoadingSpinner";
import {
  fetchLensRun,
  removeHit,
  selectLensRun,
  setLensHits,
  setLensHitsAndCount,
  setMatchHits,
} from "redux/slices/lensRun";
import HitTableWrapper from "./HitTableWrapper";
import { useSnackbar } from "notistack";
import { VirtuosoHandle } from "react-virtuoso";
import { useNavigate } from "react-router-dom";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { BrowserMode } from "routes/lenses/lens/case/CaseHitsBrowser/BrowserMode";
import { selectIntakeById, selectIntakes, selectSectionValues } from "redux/slices/case";
import FormFieldPopover from "components/FormField/FormFieldPopover";
import { CaseFormContext } from "contexts/CaseFormContext";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";

const HITS_PER_CALL = 20;

export interface CaseHitsBrowserProps {
  lensId: number;
  lensCase: Case;
  lensHitFilterState: LensHitFilterState;
}

const CaseHitsBrowser: FC<CaseHitsBrowserProps> = (props) => {
  const { searchParams } = useSearchParamsWithUpdate();
  const hideDoc = searchParams.get("hideDoc") === "true";

  const { lensId, lensCase, lensHitFilterState } = props;
  const caseId = lensCase.id;

  const [mode, setMode] = useState<BrowserMode>(() => {
    const rawMode = searchParams.get("mode");
    return rawMode === "discover" ? "discover" : "review";
  });

  const reviewMode = mode === "review";
  const discoverMode = mode === "discover";

  const dispatch = useAppDispatch();
  const snackpatch = useAppDispatchWithSnackbar();
  const { lensHits, lensHitsFullCount, matchHits, matchHitsFullCount } = useAppSelector(selectLensRun);
  const { enqueueSnackbar } = useSnackbar();

  const form: Form | undefined = useAppSelector(selectForm);
  const sectionValues = useAppSelector(selectSectionValues);

  const navigate = useNavigate();

  const intakes = useAppSelector(selectIntakes);

  // We use this to ignore responses to requests that we sent before
  // the filters were updated. We want to use the result of the last request made,
  // not the last request to complete, which is often the first request made, as it
  // is unfiltered and thus takes longer to return more data
  const latestRequestId = React.useRef(0);

  const [pinAllDialogOpen, setPinAllDialogOpen] = React.useState(false);

  const { reviewType, setReviewType } = lensHitFilterState;

  const [prescriptionFinding, setPrescriptionFinding] = React.useState<CategoryFindingSummary | undefined>();

  const [queryString, setQueryString] = React.useState<string | undefined>();
  const [queryFieldContents, setQueryFieldContents] = React.useState<string>("");

  const currentFindings = useAppSelector(selectReviewCurrentFindings);
  const categorySummaries = currentFindings?.findings || [];

  const [initialExpandedCategory, setInitialExpandedCategory] = React.useState<number | undefined>(undefined);
  const [expandedCategoryIndex, setExpandedCategoryIndex] = React.useState<number | undefined>(undefined);
  const isCategoryExpanded = expandedCategoryIndex !== undefined;
  const selectedCategory: CategoryFindingSummary | undefined = isCategoryExpanded
    ? categorySummaries[expandedCategoryIndex || 0]
    : undefined;
  const selectedCategoryRuleId = selectedCategory?.categoryId;

  const [overrideSelectedHit, setOverrideSelectedHit] = React.useState<LensHit | undefined>(undefined);

  const [loadingCategorySummaries, setLoadingCategorySummaries] = React.useState(false);

  const itemsFullCount = reviewMode
    ? reviewType === "chrono"
      ? lensHitsFullCount
      : selectedCategory?.count || 0
    : matchHitsFullCount;
  const items: Array<LensHit | MatchingBlock> = reviewMode ? lensHits : matchHits;

  const [numShowing, setNumShowing] = React.useState(0);
  const [firstShowing, setFirstShowing] = React.useState(1);
  const lastShowing = firstShowing + numShowing;

  const [itemNum, setItemNum] = React.useState(0);

  const [duplicateFindings, setDuplicateFindings] = React.useState<LensHit[]>([]);

  const itemNumIsVisible = itemNum >= firstShowing && itemNum <= lastShowing;

  const [filterAnchorEl, setFilterAnchorEl] = React.useState<HTMLElement | undefined>();

  const {
    selectedPageTypes,
    selectedClusterTypes,
    selectedPageLabels,
    selectedNotClusterTypes,
    selectedCancerStages,
    showCancerRecurrence,
    selectedStartDate,
    selectedEndDate,
    selectedIntakes,
    setSelectedIntakes,
    showNegated,
    hideDuplicates,
    startDateFormatted,
    endDateFormatted,
    selectedIntakeIds,
    selectedRuleIds,
    currentFilters,
    currentField,
    recommendedOnly,
    sortFindings,
    documentExcludedProperties,
    documentRequiredProperties,
  } = lensHitFilterState;

  const reviewConfig = useAppSelector(selectReviewConfigByField(currentField?.id));

  const numberFiltersApplied = numberOfLensHitFilters(currentFilters);
  const mainTitle = currentField
    ? `${currentField.outlinePrefix} ${currentField.name}`
    : selectedRuleIds.length === 0
      ? "Lens Categories"
      : selectedRuleIds.length === 1
        ? "1 Category"
        : `${selectedRuleIds.length} Categories`;
  const baseFindingsStatus = useAppSelector(selectReviewBaseFindingsStatus);
  const filteredFindingsStatus = useAppSelector(selectReviewFilteredFindingsStatus);
  const isLoadingFindings =
    baseFindingsStatus === RequestStatus.PENDING || filteredFindingsStatus === RequestStatus.PENDING;
  const pendingIntakes = (intakes || []).filter((intake) => intake.status === DocumentStatus.PendingReview);
  const hasSomePendingIntakes = nonEmpty(pendingIntakes) && pendingIntakes.length !== (intakes.length || 0);
  const promptFilterPending = hasSomePendingIntakes && empty(selectedIntakes);

  const lensHit: LensHit | undefined = lensHits[itemNum] ? lensHits[itemNum] : undefined;
  const matchingBlock: MatchingBlock | undefined = matchHits[itemNum] ? matchHits[itemNum] : undefined;
  const item: LensHit | MatchingBlock | undefined = reviewMode ? lensHit : matchingBlock;

  const block: Block | undefined = overrideSelectedHit ? overrideSelectedHit.block : item?.block;

  const intake = useAppSelector(selectIntakeById(block?.documentIntakeId));
  const needToFetch = lensHits.length !== 0 && itemNum + 1 >= lensHits.length && lensHits.length < lensHitsFullCount;

  const [pageNum, setPageNum] = React.useState(block?.originalPageNumber || 1);
  const duplicateBlocksForHit =
    currentField && reviewConfig && reviewConfig.medicationReview
      ? overrideSelectedHit?.collapsedHits.map((ch) => ch.blockIndex)
      : [];
  const caseForm = useContext(CaseFormContext);

  useEffect(() => {
    if (currentField) {
      caseForm.focusFieldValueGroupState[1]({ fieldId: currentField.id });
      if (currentField.reviewConfigId) {
        snackpatch(fetchReviewConfig({ fieldId: currentField.id, reviewConfigId: currentField?.reviewConfigId }), {
          errorMessage: "Failed to load review config",
        });
      }
    }
  }, [currentField?.id]);

  const [initialLoad, setInitialLoad] = React.useState(true);
  const [loadedParams, setLoadedParams] = React.useState(false);
  const [loadingHits, setLoadingHits] = React.useState(true);

  const [initialSelectedIntakes, setInitialSelectedIntakes] = React.useState<number[]>([]);

  useEffect(() => {
    if (loadedParams) {
      dispatch(fetchBaseFindings({ lensId, caseId, fieldId: currentField?.id, intakeIds: selectedIntakeIds }))
        .unwrap()
        .catch(() => {
          enqueueSnackbar("There was a problem loading the findings. Please try again later.", {
            variant: "error",
          });
        });
    }
  }, [currentField?.id, selectedIntakeIds, loadedParams]);

  const virtuosoRef = React.useRef<VirtuosoHandle>();

  const clearAndCloseFilters = (clearRules?: boolean) => {
    lensHitFilterState.clearFilters(clearRules);
    setFilterAnchorEl(undefined);
  };

  const onHitIgnored = (hit: LensHit): void => {
    dispatch(removeHit({ hitId: hit.hitId }));
    const foundHit = lensHits[itemNum + 1];
    if (foundHit) {
      setPageNum(foundHit.block.originalPageNumber);
    }
    enqueueSnackbar("Finding Dismissed", { variant: "success" });
  };

  const getItems = async (
    startPos: number,
    fetchCount: number,
    selectedCategoryId?: string,
    distinctMode?: DistinctMode
  ) => {
    if (reviewMode) {
      const rulesToUse = reviewType === "grouped" && selectedCategoryId ? [selectedCategoryId] : selectedRuleIds;
      return await backend.getLensRun({
        lensId,
        caseId,
        offset: startPos,
        limit: fetchCount,
        includeSurroundings: true,
        filters: {
          ...currentFilters,
          ruleIds: rulesToUse,
          distinctMode,
        },
      });
    } else {
      let result: BackendResponse<GetCaseMatchingBlocksResponse | GetCaseEntityBlocksResponse>;
      if (queryString) {
        result = await backend.getCaseMatchingBlocks(
          lensId,
          caseId,
          queryString,
          selectedIntakeIds,
          startDateFormatted,
          endDateFormatted,
          startPos,
          fetchCount
        );
      } else {
        result = { tag: "success", value: { fullCount: 0, blocks: [] } };
      }
      return backend.mapSuccess(result, (response) => response);
    }
  };

  const toggleCategoryExpand = async (categoryIndex: number) => {
    const finding = categorySummaries[categoryIndex];
    if (!finding) return;

    if (expandedCategoryIndex === categoryIndex) {
      // Collapse
      setExpandedCategoryIndex(undefined);
      setPrescriptionFinding(undefined);
      setItemNum(0);
    } else {
      setItemNum(0);
      setPrescriptionFinding(undefined);
      dispatch(setLensHits({ lensHits: [] }));
      setExpandedCategoryIndex(categoryIndex);
      setLoadingHits(true);

      const response = await dispatch(
        fetchLensRun({
          lensId,
          caseId,
          offset: 0,
          limit: HITS_PER_CALL,
          includeSurroundings: true,
          filters: makeLensHitFilters({ ...currentFilters, ruleIds: [finding.categoryId] }),
        })
      );
      const foundBlock = (response.payload as GetLensRunResponse).hits[itemNum];
      if (foundBlock) setPageNum(foundBlock.block.originalPageNumber);

      // Fetch Prescription Summary
      if (finding.showMedicationData && caseId !== -1) {
        try {
          const prescriptionResult = await getCaseFindings({
            lensId,
            caseId,
            filters: makeLensHitFilters({
              dateStart: startDateFormatted,
              dateEnd: endDateFormatted,
              intakeIds: selectedIntakeIds,
              rules: [finding.ruleType],
              pageTypes: [PageType.Pharmacy],
            }),
          });
          const result = prescriptionResult.case.findings.findings[0];
          if (result) {
            setPrescriptionFinding(result);
          }
        } catch (e) {
          enqueueSnackbar("There was a problem loading the prescription finding. Please try again later.", {
            variant: "error",
          });
        }
      }
      setLoadingHits(false);
    }
  };

  const reloadSummaries = () => {
    if (!loadedParams || caseId === -1) {
      return;
    }
    setLoadingCategorySummaries(true);
    dispatch(fetchFilteredFindings({ lensId, caseId, filters: currentFilters, sortBy: sortFindings }))
      .unwrap()
      .then(() => {
        setLoadingCategorySummaries(false);
        if (expandedCategoryIndex !== undefined) toggleCategoryExpand(expandedCategoryIndex);
      })
      .catch(() => {
        setLoadingCategorySummaries(false);
        enqueueSnackbar("There was a problem loading the findings. Please try again later.", {
          variant: "error",
        });
      });
  };

  const reloadItems = async () => {
    if (!loadedParams) {
      return;
    }
    if (reviewType === "grouped" && selectedCategoryRuleId === undefined) {
      return;
    }

    // if (currentField && !reviewConfig) {
    //   return;
    // }

    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;
    setLoadingHits(true);
    const response = await getItems(
      0,
      HITS_PER_CALL,
      selectedCategoryRuleId,
      reviewConfig?.medicationReview ? DistinctMode.CategoryPage : undefined
    );
    setLoadingHits(false);
    // If this is the completion of a request that has since been
    // superceded by a more recent request, ignore the response
    if (latestRequestId.current === requestId && response) {
      if (reviewMode) {
        dispatch(
          setLensHitsAndCount({
            hits: (response as GetLensRunResponse).hits,
            fullCount: response.fullCount,
            fullCountPreIdentity: (response as GetLensRunResponse).fullCountPreIdentity || response.fullCount,
          })
        );
        const foundBlock = (response as GetLensRunResponse).hits[itemNum];
        if (foundBlock) setPageNum(foundBlock.block.originalPageNumber);
      } else {
        dispatch(
          setMatchHits({
            blocks: (response as GetCaseMatchingBlocksResponse).blocks,
            fullCount: response.fullCount,
          })
        );
        const foundBlock = (response as GetCaseMatchingBlocksResponse).blocks[itemNum];
        if (foundBlock) setPageNum(foundBlock.block.originalPageNumber);
      }
      if (!initialLoad) {
        setItemNum(0);
      }
      if (initialLoad) setInitialLoad(false);
    }
  };

  const loadDuplicates = async () => {
    if (reviewMode && hideDuplicates && lensHit && lensHit?.hitId && nonEmpty(lensHit?.collapsedHits)) {
      const response = await backend.getLensRun({
        lensId,
        caseId,
        offset: 0,
        limit: Math.min(lensHit?.collapsedHits.length || 20, 100),
        includeSurroundings: true,
        filters: makeLensHitFilters({
          lensHitIds: lensHit.collapsedHits.map((ch) => ch.hitId),
        }),
      });

      setDuplicateFindings(response.hits);
    } else {
      setDuplicateFindings([]);
    }
  };

  const expandedCategoryRef = React.createRef<HTMLDivElement>();

  const handleSetDiscoverMode = () => {
    setMode("discover");
    setDuplicateFindings([]);
    setReviewType("chrono");
    setPageNum(1);
  };

  const handleSetReviewMode = () => {
    setMode("review");
    setReviewType("grouped");
  };

  React.useEffect(() => {
    reloadItems();
  }, [mode]);

  React.useEffect(() => {
    if (reviewType === "grouped" && expandedCategoryRef.current) {
      expandedCategoryRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [lensHits.length]);

  React.useEffect(() => {
    // On initial load, parse query params
    loadQueryParams(
      searchParams,
      lensHitFilterState,
      setItemNum,
      setInitialExpandedCategory,
      setInitialSelectedIntakes,
      lensHitFilterState.clearFilters,
      setMode,
      setQueryString,
      setQueryFieldContents
    );
    setLoadedParams(true);
  }, []);

  React.useEffect(() => {
    return () => {
      dispatch(
        setLensHitsAndCount({
          hits: [],
          fullCount: 0,
          fullCountPreIdentity: 0,
        })
      );
    };
  }, []);

  // One-time set of query params from holding state once docs load
  React.useEffect(() => {
    if (nonEmpty(initialSelectedIntakes) && nonEmpty(intakes)) {
      setSelectedIntakes(intakes.filter((d) => initialSelectedIntakes.indexOf(d.id) !== -1));
      setInitialSelectedIntakes([]);
    }
  }, [intakes]);

  // One-time expand of category from query param once summaries are loaded
  React.useEffect(() => {
    if (nonEmpty(categorySummaries) && initialExpandedCategory !== undefined) {
      toggleCategoryExpand(initialExpandedCategory);
      setInitialExpandedCategory(undefined);
    }
  }, [categorySummaries]);

  useEffect(() => {
    if (reviewConfig) {
      if (reviewConfig.hideNegated) {
        lensHitFilterState.setShowNegated(false);
      }
      if (reviewConfig.showFamily) {
        lensHitFilterState.setSelectedClusterTypes([ClusterType.FamilyHistory]);
      }
      if (nonEmpty(reviewConfig.excludedClusterTypes)) {
        lensHitFilterState.setSelectedNotClusterTypes(reviewConfig.excludedClusterTypes);
      }
      if (nonEmpty(reviewConfig.requiredCustomProperties)) {
        lensHitFilterState.setDocumentRequiredProperties(reviewConfig.requiredCustomProperties);
      }
      if (nonEmpty(reviewConfig.excludedCustomProperties)) {
        lensHitFilterState.setDocumentExcludedProperties(reviewConfig.excludedCustomProperties);
      }
      if (nonEmpty(reviewConfig.excludedPageTypes)) {
        lensHitFilterState.setSelectedNotPageTypes(reviewConfig.excludedPageTypes);
      }
      if (nonEmpty(reviewConfig.excludedLabelNames)) {
        lensHitFilterState.setSelectedNotPageLabels(reviewConfig.excludedLabelNames);
      }
    }
  }, [reviewConfig]);

  // Sync query params
  React.useEffect(() => {
    const queryParams = calculateQueryParams(lensHitFilterState, itemNum, expandedCategoryIndex, mode, queryString);
    // We do not use router setSearchParams hook here
    // as it causes search params to update in router before we navigate away with breadcrumbs
    window.history.replaceState({}, "", `${window.location.pathname}${queryParams.toString()}`);
  }, [
    itemNum,
    currentField?.id,
    expandedCategoryIndex,
    selectedIntakes,
    selectedRuleIds,
    selectedClusterTypes,
    selectedNotClusterTypes,
    selectedPageTypes,
    showNegated,
    mode,
    queryString,
    reviewType,
  ]);

  React.useEffect(() => {
    if (!filterAnchorEl) {
      reloadItems();
      loadDuplicates();
      if (expandedCategoryIndex === undefined) {
        reloadSummaries();
      }
    }
  }, [
    loadedParams,
    currentField?.id,
    reviewConfig,
    selectedIntakes,
    selectedRuleIds,
    documentRequiredProperties,
    documentExcludedProperties,
    queryString,
    reviewType,
    selectedPageTypes,
    selectedClusterTypes,
    selectedPageLabels,
    selectedNotClusterTypes,
    showNegated,
    hideDuplicates,
    selectedCancerStages,
    showCancerRecurrence,
    selectedStartDate,
    selectedEndDate,
    recommendedOnly,
    lensId,
    caseId,
    sortFindings,
  ]);

  const allLoaded = items.length >= itemsFullCount;

  const fetchMore = async (scrollToHit: boolean) => {
    const fetched = items.length;
    if (!allLoaded) {
      setLoadingHits(true);
      const needSelected = itemNum > fetched;
      const offBy = itemNum - fetched;
      const fetchCount = needSelected ? offBy + 3 : HITS_PER_CALL;
      const result = await getItems(fetched, fetchCount, selectedCategoryRuleId);
      setLoadingHits(false);
      if (result) {
        let newHits: Array<LensHit | MatchingBlock>;
        if (reviewMode) {
          const hits = distinct(
            lensHits.concat((result as GetLensRunResponse).hits),
            (h1, h2) => h1.hitId === h2.hitId
          );
          newHits = hits;
          dispatch(setLensHits({ lensHits: hits }));
        } else {
          const blocks = distinct(
            matchHits.concat((result as GetCaseMatchingBlocksResponse).blocks),
            (h1, h2) => h1.block.documentId === h2.block.documentId && h1.block.index === h2.block.index
          );
          newHits = blocks;
          dispatch(setMatchHits({ blocks, fullCount: result.fullCount }));
        }
        const foundBlock = newHits[itemNum];
        if (foundBlock) {
          setPageNum(foundBlock.block.originalPageNumber);
        }
        const hitNumIsLoaded = itemNum <= newHits.length;
        if (!itemNumIsVisible && scrollToHit && hitNumIsLoaded) {
          setTimeout(() => {
            if (virtuosoRef.current) {
              virtuosoRef.current.scrollToIndex({
                index: itemNum,
              });
            }
          }, 500);
        }
      }
    }
  };

  useEffect(() => {
    if (needToFetch) {
      fetchMore(true);
    }
  }, [needToFetch]);

  useEffect(() => {
    setOverrideSelectedHit(undefined);
    loadDuplicates();
  }, [lensHit?.hitId]);

  const doTextSearch = () => {
    const value = queryFieldContents.trim();
    if (value === "") return;
    setQueryString(value);
  };

  const clearFiltersAndReload = (clearRules?: boolean) => {
    clearAndCloseFilters(clearRules);
    reloadItems();
  };

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    const t = event.currentTarget;
    setFilterAnchorEl(t);
  };

  const selectItem = (num: number) => {
    setOverrideSelectedHit(undefined);
    setItemNum(num);
  };

  const updateReviewType = (typ: "chrono" | "grouped") => {
    selectItem(0);
    setFirstShowing(1);
    setNumShowing(0);
    setReviewType(typ);
    setExpandedCategoryIndex(undefined);
    if (typ === "grouped") {
      dispatch(setLensHits({ lensHits: [] }));
    }
  };

  const lensHitField: FormField | undefined = lensHit?.formFieldId
    ? form?.fields.find((f) => f.id === lensHit?.formFieldId)
    : undefined;

  const onPinAll = async () => {
    await reloadItems();
  };

  const filterButton = (
    <Tooltip arrow title={`${numberFiltersApplied} filters applied`}>
      <IconButton id="case-review-filter" onClick={handleOpenPopover}>
        <Badge badgeContent={numberFiltersApplied} color="primary">
          <FilterListIcon />
        </Badge>
      </IconButton>
    </Tooltip>
  );

  const banner = (
    <Flex pb={2}>
      <Flex flexDirection="row" alignItems="center">
        <Button
          color={mode === "review" ? "primary" : "default"}
          size="medium"
          onClick={() => handleSetReviewMode()}
          startIcon={<ViewStreamIcon />}>
          Lens Hits
        </Button>
        <Button
          color={mode === "discover" ? "primary" : "default"}
          size="medium"
          onClick={() => handleSetDiscoverMode()}
          startIcon={<SearchIcon />}>
          Free Search
        </Button>
        <Flex flexDirection="row" alignItems="center">
          {promptFilterPending && (
            <Button
              color="primary"
              variant="contained"
              size="small"
              sx={{ ml: 1 }}
              onClick={() => setSelectedIntakes(pendingIntakes)}
              startIcon={<InfoIcon />}>
              New Documents Only
            </Button>
          )}
          {isLoadingFindings && <LoadingSpinner size={24} sx={{ p: 1 }} />}
        </Flex>
      </Flex>
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(undefined)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}>
        <Flex p={2} width={600}>
          <LensHitFilterControls
            showRules={reviewMode && expandedCategoryIndex === undefined}
            lensId={lensId}
            caseId={caseId}
            reviewConfig={reviewConfig}
            filterState={lensHitFilterState}
            showLensCounts={true}
          />
        </Flex>
        <Flex flexDirection="row" justifyContent="flex-end" p={2}>
          <Button
            color="default"
            onClick={() => clearFiltersAndReload(expandedCategoryIndex === undefined)}
            style={{ marginRight: 8 }}>
            Clear Filters
          </Button>
          <Button
            id="hits_browser_filters_go"
            variant="outlined"
            color="default"
            onClick={() => {
              setFilterAnchorEl(undefined);
              reloadItems();
              loadDuplicates();
              if (expandedCategoryIndex === undefined) {
                reloadSummaries();
              }
            }}>
            Go
          </Button>
        </Flex>
      </Popover>
      {discoverMode && (
        <Flex flexDirection="row" mt={2}>
          <TextField
            label="Free search"
            fullWidth
            value={queryFieldContents}
            onChange={(evt) => setQueryFieldContents(evt.target.value)}
            onKeyDown={(evt) => {
              if (evt.keyCode === 13) doTextSearch();
            }}
            onFocus={(evt) => evt.target.select()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={doTextSearch} size="small">
                    <SearchIcon color="primary" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {filterButton}
        </Flex>
      )}
    </Flex>
  );

  const control =
    reviewType === "chrono" ? (
      <>
        {mode === "review" && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", maxWidth: "100%", flexWrap: "nowrap", minWidth: 0 }}>
              <CaseHitsBreadcrumbs
                allCategoriesClick={() => updateReviewType("grouped")}
                activeCategory="All Hits"
                titleText={mainTitle}
                currentSection={sectionValues.find((sv) => sv.sectionId === currentField?.sectionId)}
              />
              {form !== undefined && currentField?.id !== undefined && (
                <FormFieldPopover
                  form={form}
                  fieldId={currentField.id}
                  hideLightbulb
                  navigateToBlock={(intakeId, page, _blockIndex) => {
                    navigate("../documents/" + intakeId + "?page=" + page);
                  }}
                />
              )}
            </Box>
            <P
              variant="xsBold"
              sx={{
                whiteSpace: "nowrap",
              }}>
              {displayDateLoose(currentFindings?.minDate)}{" "}
              {currentFindings?.maxDate ? `- ${displayDateLoose(currentFindings?.maxDate)}` : ""}
            </P>
          </Box>
        )}
        {currentField && reviewConfig && reviewConfig.medicationReview && reviewMode && (
          <HitTableWrapper
            formField={currentField}
            lensId={lensId}
            caseId={caseId}
            intake={intake}
            lensHitFilterState={lensHitFilterState}
            overrideHitHandle={[overrideSelectedHit, setOverrideSelectedHit]}
            selectedCategoryRuleId={selectedCategoryRuleId}
            setPageNum={setPageNum}
          />
        )}
        {(!reviewConfig?.medicationReview || discoverMode) && (
          <HitCardVirtuoso
            onHitIgnored={onHitIgnored}
            lensId={lensId}
            caseId={caseId}
            intake={intake}
            items={items}
            setPageNum={setPageNum}
            showTitle
            itemsFullCount={itemsFullCount}
            loading={loadingHits}
            numShowingHandle={[numShowing, setNumShowing]}
            firstShowingHandle={[firstShowing, setFirstShowing]}
            itemNumHandle={[itemNum, setItemNum]}
            duplicateFindings={hideDuplicates ? duplicateFindings : []}
            fetchMore={fetchMore}
            overrideHitState={[overrideSelectedHit, setOverrideSelectedHit]}
            endComponent={
              <Flex marginBottom={15}>
                <Typography>
                  <b>End</b>
                </Typography>
              </Flex>
            }
          />
        )}
      </>
    ) : (
      <>
        {selectedCategory && expandedCategoryIndex !== undefined && (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", pr: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", maxWidth: "100%", flexWrap: "nowrap", minWidth: 0 }}>
                <CaseHitsBreadcrumbs
                  allCategoriesClick={() => toggleCategoryExpand(expandedCategoryIndex)}
                  activeCategory={`${selectedCategory.ruleType.subtype} - ${selectedCategory.ruleType.description}`}
                  titleText={mainTitle}
                  currentSection={sectionValues.find((sv) => sv.sectionId === currentField?.sectionId)}
                />
                {form !== undefined && currentField?.id !== undefined && (
                  <FormFieldPopover
                    form={form}
                    fieldId={currentField.id}
                    hideLightbulb
                    navigateToBlock={(intakeId, page, _blockIndex) => {
                      navigate("../documents/" + intakeId + "?page=" + page);
                    }}
                  />
                )}
              </Box>
              <Box>
                <P
                  variant="xsBold"
                  sx={{
                    whiteSpace: "nowrap",
                  }}>
                  {categoryDateRange(selectedCategory)}
                </P>
              </Box>
            </Box>
            {selectedCategory.showMedicationData && prescriptionFinding && prescriptionFinding.count > 0 && (
              <PharmacyFindings
                prescriptionFinding={prescriptionFinding}
                onViewFindings={() => setPinAllDialogOpen(true)}
              />
            )}
            {currentField && reviewConfig && reviewConfig.medicationReview && (
              <HitTableWrapper
                formField={currentField}
                lensId={lensId}
                caseId={caseId}
                intake={intake}
                overrideHitHandle={[overrideSelectedHit, setOverrideSelectedHit]}
                lensHitFilterState={lensHitFilterState}
                selectedCategoryRuleId={selectedCategoryRuleId}
                setPageNum={setPageNum}
              />
            )}
            {!reviewConfig?.medicationReview && (
              <HitCardVirtuoso
                onHitIgnored={onHitIgnored}
                lensId={lensId}
                caseId={caseId}
                intake={intake}
                setPageNum={setPageNum}
                items={lensHits}
                showTitle
                itemsFullCount={lensHitsFullCount}
                loading={loadingHits}
                numShowingHandle={[numShowing, setNumShowing]}
                firstShowingHandle={[firstShowing, setFirstShowing]}
                itemNumHandle={[itemNum, setItemNum]}
                duplicateFindings={hideDuplicates ? duplicateFindings : []}
                fetchMore={fetchMore}
                overrideHitState={[overrideSelectedHit, setOverrideSelectedHit]}
                endComponent={
                  <Flex>
                    <Button color="default" onClick={() => setExpandedCategoryIndex(undefined)}>
                      Back
                    </Button>
                  </Flex>
                }
              />
            )}
          </>
        )}
        {reviewMode && expandedCategoryIndex === undefined && (
          <Card sx={{ display: "flex", flexDirection: "column" }}>
            <CardHeader
              title={
                reviewMode && form ? (
                  <ProfileBasedReviewControl
                    form={form}
                    filterState={lensHitFilterState}
                    reviewConfig={reviewConfig}
                    filterButton={filterButton}
                  />
                ) : (
                  mainTitle
                )
              }
              sx={{ pb: 0 }}
            />
            <CardContent sx={{ overflow: "auto" }}>
              {(categorySummaries.length > 0 || loadingCategorySummaries) && currentFindings ? (
                <LensCategories
                  findings={currentFindings}
                  loadingHits={loadingHits}
                  hideDuplicates={hideDuplicates}
                  lensHitsFullCount={lensHitsFullCount}
                  expandedCategoryIndex={expandedCategoryIndex}
                  onCategoryClicked={(idx) => toggleCategoryExpand(idx)}
                  onAllHitsClicked={() => updateReviewType("chrono")}
                />
              ) : (
                <P variant="body3">
                  No results found.
                  {numberFiltersApplied > 0 && (
                    <>
                      {" Try "}
                      <Button
                        variant="outlined"
                        sx={{ ml: 0.5 }}
                        size="small"
                        onClick={() => clearFiltersAndReload(expandedCategoryIndex === undefined)}>
                        Clearing Filters
                      </Button>
                    </>
                  )}
                </P>
              )}
            </CardContent>
          </Card>
        )}
        {selectedCategory && lensHitField && (
          <PinHitsTableDialog
            lensId={lensId}
            caseId={caseId}
            selectedCategory={selectedCategory}
            openHandle={[pinAllDialogOpen, setPinAllDialogOpen]}
            onPin={onPinAll}
            suggestedField={lensHitField}
            lensHitFilterState={lensHitFilterState}
          />
        )}
      </>
    );

  const document = intake ? (
    <IntakeViewer
      lensId={lensId}
      caseId={caseId}
      parentBlockIndex={block?.index}
      additionalAnnotations={[]}
      intake={intake}
      pageNumberHandle={[pageNum, setPageNum]}
      parentLensHit={lensHit}
      knownFieldId={lensHit?.formFieldId}
      duplicateBlocksForHit={duplicateBlocksForHit}
      pinForm={{
        mode: "panel",
        cardFormFragmentProps: {
          compact: true,
        },
      }}
    />
  ) : (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
      <Box sx={{ textAlign: "center" }}>
        <P variant="h4">No document selected</P>
        <P variant="body5" sx={{ color: "grey.500" }}>
          {reviewType === "grouped"
            ? "Select a finding to activate document viewer"
            : "Enter a search and select a finding to activate the document viewer"}
        </P>
      </Box>
    </Box>
  );

  return <HitsBrowserLayout banner={banner} control={control} document={document} hideDoc={hideDoc} />;
};

export default CaseHitsBrowser;
